import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Play, Headphones, Download, ShoppingCart, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { resourcesData, resourceCategories, type ResourceItem } from '@/data/resourcesData';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function Resources() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredResources, setFilteredResources] = useState<ResourceItem[]>(resourcesData);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const itemsPerPage = 12;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let filtered = resourcesData;

    if (selectedCategory !== '全部') {
      filtered = filtered.filter((r) => r.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (r) =>
          r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredResources(filtered);
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResources = filteredResources.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        {/* Banner */}
        <section className="relative h-[280px] md:h-[350px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1920&h=500&fit=crop)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1c5bff]/80 to-[#1c5bff]/40" />
          <div className="relative h-full section-container flex items-center">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">资源中心</h1>
              <p className="text-xl text-white/90">海量音视频资源，助力企业成长</p>
            </div>
          </div>
        </section>

        {/* Search & Filter */}
        <section className="bg-white border-b border-gray-100 py-6">
          <div className="section-container">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1 max-w-xl">
                <input
                  type="text"
                  placeholder="搜索资源名称、标签..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-11 pl-12 pr-4 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
              <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
              {resourceCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-[#1c5bff] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Resources Grid */}
        <section ref={sectionRef} className="py-12 bg-gray-50">
          <div className="section-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedResources.map((resource, index) => (
                <div
                  key={resource.id}
                  className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{
                    transitionTimingFunction: 'var(--ease-expo-out)',
                    transitionDelay: `${(index % 4) * 50}ms`,
                  }}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={resource.thumbnail}
                      alt={resource.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors">
                        {resource.type === 'video' ? (
                          <Play className="w-6 h-6 text-[#1c5bff] ml-1" />
                        ) : (
                          <Headphones className="w-6 h-6 text-[#1c5bff]" />
                        )}
                      </button>
                    </div>
                    <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded flex items-center gap-1">
                      {resource.type === 'video' ? <Play className="w-3 h-3" /> : <Headphones className="w-3 h-3" />}
                      {resource.duration}
                    </div>
                    {resource.originalPrice && (
                      <span className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs rounded">
                        优惠
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-[#1c5bff]/10 text-[#1c5bff] text-xs rounded">
                        {resource.category}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#1c5bff] transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                      {resource.description}
                    </p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {resource.tags.slice(0, 2).map((tag, i) => (
                        <span key={i} className="text-xs text-gray-400">#{tag}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-end gap-2">
                        <span className="text-lg font-bold text-[#1c5bff]">
                          {formatPrice(resource.price)}
                        </span>
                        {resource.originalPrice && (
                          <span className="text-sm text-gray-400 line-through">
                            {formatPrice(resource.originalPrice)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-400">
                        <Download className="w-4 h-4" />
                        {resource.downloadCount}
                      </div>
                    </div>
                    <button className="w-full mt-4 py-2 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center justify-center gap-2">
                      <ShoppingCart className="w-4 h-4" />
                      立即购买
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredResources.length === 0 && (
              <div className="text-center py-16">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">未找到相关资源</p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('全部');
                  }}
                  className="mt-4 text-[#1c5bff] hover:underline"
                >
                  清除筛选条件
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all duration-300 ${
                      page === currentPage
                        ? 'bg-[#1c5bff] text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {filteredResources.length > 0 && (
              <p className="text-center text-gray-500 text-sm mt-4">
                共 {filteredResources.length} 个资源，当前第 {currentPage}/{totalPages} 页
              </p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
